import {
  faSort,
  faSortAmountDown,
  faSortAmountUp,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import Pagination, { IPagination } from '../pagination/Pagination';
import Row from './Row';

type Sortable =
  | {
      onSort: (dir: string) => void | Promise<void>;
    }
  | {
      onSort?: never;
    };

export type DataTableColumn<T> = {
  name: string;
  width?: string;
  className?: string;
} & Sortable &
  (
    | {
        selector: keyof T;
        render?: never;
        placeholder?: never;
      }
    | {
        selector?: never;
        render: (row: T) => React.ReactNode;
        placeholder?: never;
      }
    | {
        selector?: never;
        render?: never;
        placeholder: true;
      }
  );

interface IDataTable<T> {
  className?: string;
  columns: DataTableColumn<T>[];
  data: T[];
  header?: React.ReactNode | null;
  keyField: keyof T | ((row: T, idx: number) => string | number);
  /**
   * If defined, a column is prepended to the table for expanding & collapsing each row.
   * If returned React Node is null, row expanding is disabled.
   */
  collapsible?: (row: T) => React.ReactNode | null;
  isRowCollapsible?: boolean;
  pagination?: IPagination;
  filter?: (row: T) => boolean;
  collapsibleHolder?: boolean;
  selectable?: boolean;
  onSelectAll?: (checked: boolean) => void;
  onSelect?: (checked: boolean, row: T, selectedRows: number[]) => void;
  onGetSelectedRows?: () => number[];
  selected?: T[];
  isLoading?: boolean;
  responsive?: boolean;
}

const DataTable = <T extends Record<string, unknown>>(props: IDataTable<T>) => {
  const { columns, data, keyField } = props;
  const [sortField, setSortField] = useState({ colName: '', icon: faSort });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const handleSort = (colName: string, sortFunc: Sortable['onSort']) => {
    if (sortFunc) {
      // starting icon before click
      let icon: IconDefinition;
      if (sortField.colName === colName)
        icon = sortField.icon ? sortField.icon : faSort;
      else icon = faSort;

      // go to next icon
      if (icon.iconName === faSort.iconName) icon = faSortAmountUp;
      else if (icon.iconName === faSortAmountUp.iconName)
        icon = faSortAmountDown;
      else {
        colName = '';
        icon = faSort;
      }
      setSortField({ colName: colName, icon: icon });

      let sortDir;
      if (icon.iconName === faSortAmountDown.iconName) {
        sortDir = 'DESC';
      } else {
        sortDir = 'ASC';
      }
      sortFunc(sortDir);
    }
  };

  // const handleBulkSelect = (event: React.ChangeEvent<HTMLInputElement>) =>
  //   props.onSelectAll && props.onSelectAll(event.target.checked);

  const handleRowSelection = (checked: boolean, row: any) => {
    const rowId = row[keyField] as number;
    if (checked) {
      setSelectedRows((prev) => {
        const newArray = [...prev, rowId];
        if (props.onSelect) {
          props.onSelect(checked, row, newArray);
        }
        return newArray;
      });
    } else {
      setSelectedRows((prev) => {
        const newArray = [...prev.filter((id) => id !== rowId)];
        if (props.onSelect) {
          props.onSelect(checked, row, newArray);
        }
        return newArray;
      });
    }
  };

  const { collapsible } = props;
  // The entire table is considered "collapsible" if at least one row is
  // collapsible. This ensures that the columns are all aligned after the collapsible button
  // column is inserted.
  const hasPrependCol =
    props.selectable ||
    props.collapsibleHolder ||
    (collapsible && props.data.some((row) => collapsible(row)));

  return (
    <div>
      <Table className={props.className} responsive={props.responsive}>
        {props.header === undefined ? (
          <thead>
            <tr>
              {hasPrependCol && (
                <th>
                  {/* {props.selectable && (
                    <FormCheck
                      onChange={handleBulkSelect}
                      checked={
                        props.data.length !== 0 &&
                        props.data.length === props.selected?.length
                      }
                    />
                  )} */}
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.name}
                  style={{ width: c.width }}
                  className={c.className}
                >
                  {c.onSort ? (
                    <Button
                      variant='link'
                      className='p-0 text-nowrap'
                      onClick={() => handleSort(c.name, c.onSort)}
                    >
                      {c.name}
                      <FontAwesomeIcon
                        className='ml-2'
                        icon={
                          c.name === sortField.colName ? sortField.icon : faSort
                        }
                      />
                    </Button>
                  ) : (
                    c.name
                  )}
                </th>
              ))}
            </tr>
          </thead>
        ) : (
          props.header
        )}
        <tbody>
          {data.filter(props.filter || (() => true)).map((row, idx) => (
            <Row
              data={row}
              columns={columns}
              key={idx}
              collapsible={props.collapsible}
              hasPrependCol={Boolean(hasPrependCol)}
              isRowCollapsible={props.isRowCollapsible}
              onSelect={(checked) => {
                if (props.onSelect) {
                  handleRowSelection(checked, row);
                }
              }}
              selectable={props.selectable}
              selected={
                (props.selected &&
                  props.selected.some(
                    (e) =>
                      getKeyVal(e, keyField, idx) ===
                      getKeyVal(row, keyField, idx)
                  )) ||
                selectedRows.includes(Number(getKeyVal(row, keyField, idx)))
              }
            />
          ))}
        </tbody>
      </Table>
      {props.pagination && <Pagination {...props.pagination} />}
    </div>
  );
};

function getKeyVal<T>(
  row: T,
  keyField: keyof T | ((row: T, idx: number) => string | number),
  idx: number
): string {
  if (typeof keyField === 'function') {
    return (row as Record<string, unknown>)[keyField(row, idx)] as string;
  }
  if (Array.isArray(row[keyField])) {
    return JSON.stringify(row[keyField]);
  }
  return row[keyField] as unknown as string;
}

export default DataTable;
